import * as React from 'react'
import { HelpCircle, Minus, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/**
 * KPI tile — compacto (4.7c). Borda superior na cor semântica + ícone + valor
 * + status opcional. Layout denso (p-3), tipografia enxuta — padrão de
 * dashboard moderno.
 *
 * `value === null` → "N/A" + microcopy (distingue "sem dado" de "0").
 */
export type KpiAccent = 'emerald' | 'rose' | 'amber' | 'blue' | 'sky' | 'violet' | 'slate'

/**
 * Redesign visual (2026-07-31, user request — referência "PAINEL COMERCIAL
 * — IA SDR") — o ícone passa de um traço fino no canto pra um badge
 * circular colorido (fundo tonal + ícone na cor forte), estilo cartão de
 * dashboard executivo. Mantém as mesmas 7 cores semânticas já usadas em
 * todo o resto do dashboard (donuts, gauge, gráficos) — só muda ONDE/COMO a
 * cor aparece no card, não introduz uma paleta nova.
 */
const ACCENT_ICON_BADGE: Record<KpiAccent, string> = {
  emerald: 'bg-emerald-500 text-white',
  rose: 'bg-rose-500 text-white',
  amber: 'bg-amber-500 text-white',
  blue: 'bg-blue-500 text-white',
  sky: 'bg-sky-500 text-white',
  violet: 'bg-violet-500 text-white',
  slate: 'bg-slate-400 text-white dark:bg-slate-600',
}

const STATUS_DOT: Record<'good' | 'watch' | 'neutral', string> = {
  good: 'bg-emerald-500',
  watch: 'bg-amber-500',
  neutral: 'bg-slate-300',
}

export interface KpiCardProps {
  title: string
  description?: string
  value: number | null
  formatValue?: (n: number) => string
  microcopy?: string
  accent?: KpiAccent
  icon?: LucideIcon
  status?: { tone: 'good' | 'watch' | 'neutral'; label: string }
  /**
   * Explica exatamente o que o número conta (fonte/filtro), num tooltip
   * acessível via ícone de "?" ao lado do título. Adicionado 2026-07-24 —
   * cards com nomes parecidos ("Agendadas" vs "Agendamentos") mas lógica bem
   * diferente confundiam o operador; isso deixa a definição explícita sem
   * poluir o card com texto sempre visível.
   */
  helpText?: string
  /**
   * Story 10.7 (2026-07-30, user request, AC1) — variação % vs. o período
   * imediatamente anterior de mesma duração. `changePct: null` renderiza
   * "N/A" (sem baseline no período anterior, mesma convenção "N/A em vez de
   * número enganoso" já usada em `value === null`). Omitido = card sem
   * comparação de período (comportamento anterior, inalterado).
   */
  trend?: { changePct: number | null }
}

const DEFAULT_MICROCOPY = 'Sem reuniões finalizadas no período.'

function defaultFormat(n: number): string {
  return n.toLocaleString('pt-BR')
}

function TrendBadge({ changePct }: { changePct: number | null }): React.ReactElement {
  if (changePct === null) {
    return (
      <span
        className="flex items-center gap-0.5 text-[10px] text-muted-foreground"
        data-testid="kpi-trend"
      >
        N/A
      </span>
    )
  }
  const pct = Math.round(changePct * 100)
  if (pct === 0) {
    return (
      <span
        className="flex items-center gap-0.5 text-[10px] text-muted-foreground"
        data-testid="kpi-trend"
      >
        <Minus className="h-3 w-3" aria-hidden="true" />
        0%
      </span>
    )
  }
  const isUp = pct > 0
  return (
    <span
      className={cn(
        'flex items-center gap-0.5 text-[10px] font-medium tabular-nums',
        isUp ? 'text-emerald-600' : 'text-rose-600',
      )}
      data-testid="kpi-trend"
    >
      {isUp ? (
        <TrendingUp className="h-3 w-3" aria-hidden="true" />
      ) : (
        <TrendingDown className="h-3 w-3" aria-hidden="true" />
      )}
      {isUp ? '+' : ''}
      {pct}%
    </span>
  )
}

export function KpiCard({
  title,
  description,
  value,
  formatValue = defaultFormat,
  microcopy = DEFAULT_MICROCOPY,
  accent = 'slate',
  icon: Icon,
  status,
  helpText,
  trend,
}: KpiCardProps): React.ReactElement {
  const isNoData = value === null
  const displayValue = isNoData ? 'N/A' : formatValue(value)

  return (
    <Card className="transition-shadow hover:shadow-sm">
      <div className="flex flex-col gap-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {title}
            </span>
            {helpText !== undefined && helpText !== '' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 text-muted-foreground/70 hover:text-foreground"
                    aria-label={`O que é "${title}"?`}
                  >
                    <HelpCircle className="h-3 w-3" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{helpText}</TooltipContent>
              </Tooltip>
            )}
          </span>
          {Icon !== undefined && (
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                ACCENT_ICON_BADGE[accent],
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          )}
        </div>

        <div className="flex items-baseline justify-between gap-1.5">
          <span className="text-2xl font-semibold leading-none tabular-nums">{displayValue}</span>
          {trend !== undefined && !isNoData && <TrendBadge changePct={trend.changePct} />}
          {trend === undefined && status !== undefined && !isNoData && (
            <span className="flex items-center gap-1 whitespace-nowrap text-[10px] text-muted-foreground">
              <span className={cn('inline-block h-1.5 w-1.5 rounded-full', STATUS_DOT[status.tone])} />
              {status.label}
            </span>
          )}
        </div>

        {description !== undefined && description !== '' && (
          <span className="truncate text-[11px] text-muted-foreground">{description}</span>
        )}
        {isNoData && (
          <span className="text-[11px] text-muted-foreground" data-testid="kpi-microcopy">
            {microcopy}
          </span>
        )}
      </div>
    </Card>
  )
}

export default KpiCard
